import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, computed, inject, input, output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { LocalizationService } from '../../../core/localization/localization.service';
import { AinFormFieldComponent } from '../../../shared/design-system/form-field.component';
import {
  FormConditional,
  FormLayoutType,
  FormSchemaModel,
  FormSection,
  createSectionKey,
  localizedText,
  readLocalizedText,
  writeLocalizedText
} from '../../../shared/dynamic-form/form-schema.models';
import { FormBuilderEditCultureService } from './form-builder-edit-culture.service';
import { FormBuilderLocalizedInputComponent } from './form-builder-localized-input.component';
import { FormBuilderVisibilityPanelComponent } from './form-builder-visibility-panel.component';

@Component({
  standalone: true,
  selector: 'ain-form-builder-section-manager',
  imports: [FormsModule, ButtonModule, InputTextModule, InputNumberModule, DragDropModule, AinFormFieldComponent, FormBuilderVisibilityPanelComponent, FormBuilderLocalizedInputComponent],
  template: `
    <section class="section-manager card">
      <div class="toolbar">
        <h4>{{ sectionLabel() }}</h4>
        @if (canEdit()) {
          <button pButton type="button" severity="secondary" icon="pi pi-plus" [label]="addLabel()" (click)="addSection()"></button>
        }
      </div>

      <div
        cdkDropList
        cdkDropListLockAxis="y"
        (cdkDropListDropped)="onSectionDrop($event)"
        class="section-list">
        @for (section of schema().sections; track section.key) {
          <article
            class="section-row"
            cdkDrag
            [cdkDragDisabled]="!canEdit()"
            [class.active]="section.key === activeSectionKey()">
            <div class="row-main">
              @if (canEdit()) {
                <span class="drag-handle" cdkDragHandle [attr.aria-label]="t('dynamic.builder.section.dragHandle')"><i class="pi pi-bars" aria-hidden="true"></i></span>
              }
              <button type="button" class="section-select" (click)="activeSectionChange.emit(section.key)">
                <strong>{{ readTitle(section) || section.key }}</strong>
                <span>{{ section.fields.length }} {{ t('dynamic.builder.section.fields') }}</span>
              </button>
            </div>

            @if (canEdit() && section.key === activeSectionKey()) {
              <div class="row-editor">
                <ain-form-builder-localized-input
                  [label]="t('dynamic.builder.section.title')"
                  [text]="section.title"
                  [disabled]="!canEdit()"
                  (textChange)="updateSectionTitleLocalized(section.key, $event)" />
                @if (showColumns()) {
                  <ain-form-field [label]="t('dynamic.builder.section.columns')">
                    <p-inputNumber
                      [ngModel]="section.columns ?? defaultColumns()"
                      (ngModelChange)="updateSectionColumns(section.key, $event)"
                      [min]="1"
                      [max]="12"
                      [useGrouping]="false" />
                  </ain-form-field>
                }
                @if (schema().sections.length > 1) {
                  <button
                    pButton
                    type="button"
                    severity="danger"
                    text
                    icon="pi pi-trash"
                    [label]="t('dynamic.builder.section.delete')"
                    (click)="deleteSection.emit(section.key)"></button>
                }
                <ain-form-builder-visibility-panel
                  [schema]="schema()"
                  [culture]="culture()"
                  [canEdit]="canEdit()"
                  [showRequireWhenVisible]="false"
                  [conditional]="section.conditional"
                  (conditionalChange)="updateSectionConditional(section.key, $event)" />
              </div>
            }
          </article>
        }
      </div>
    </section>
  `,
  styles: [`
    .section-manager { display: grid; gap: 12px; }
    .toolbar { display: flex; justify-content: space-between; align-items: center; gap: 8px; }
    .toolbar h4 { margin: 0; }
    .section-list { display: grid; gap: 8px; }
    .section-row {
      border: 1px solid #e2e8f0;
      border-radius: 10px;
      padding: 10px;
      background: #fff;
    }
    .section-row.active { border-color: #0f766e; background: #ecfdf5; }
    .row-main { display: flex; align-items: center; gap: 8px; }
    .drag-handle { cursor: grab; color: #64748b; }
    .section-select {
      display: grid;
      gap: 2px;
      border: 0;
      background: transparent;
      text-align: start;
      cursor: pointer;
      flex: 1;
    }
    .section-select span { color: #64748b; font-size: 0.8rem; }
    .row-editor {
      display: grid;
      gap: 8px;
      margin-top: 10px;
      padding-top: 10px;
      border-top: 1px solid #e2e8f0;
    }
    .card {
      border: 1px solid #e2e8f0;
      border-radius: 12px;
      padding: 12px;
      background: #fff;
    }
  `]
})
export class FormBuilderSectionManagerComponent {
  private readonly localization = inject(LocalizationService);
  private readonly editCultureService = inject(FormBuilderEditCultureService);

  readonly schema = input.required<FormSchemaModel>();
  readonly culture = input.required<string>();
  readonly layoutType = input.required<FormLayoutType>();
  readonly activeSectionKey = input<string | null>(null);
  readonly canEdit = input(true);

  readonly schemaChange = output<FormSchemaModel>();
  readonly activeSectionChange = output<string>();
  readonly deleteSection = output<string>();

  readonly showColumns = computed(() =>
    ['twoColumn', 'threeColumn', 'grid'].includes(this.layoutType())
  );

  sectionLabel(): string {
    switch (this.layoutType()) {
      case 'wizard':
        return this.t('dynamic.builder.section.steps');
      case 'tabbed':
        return this.t('dynamic.builder.section.tabs');
      default:
        return this.t('dynamic.builder.section.sections');
    }
  }

  addLabel(): string {
    switch (this.layoutType()) {
      case 'wizard':
        return this.t('dynamic.builder.section.addStep');
      case 'tabbed':
        return this.t('dynamic.builder.section.addTab');
      default:
        return this.t('dynamic.builder.section.add');
    }
  }

  defaultColumns(): number {
    return this.schema().layout.columns ?? 1;
  }

  readTitle(section: FormSection): string {
    return readLocalizedText(section.title, this.editCultureService.editCulture());
  }

  addSection(): void {
    const schema = this.schema();
    const key = createSectionKey(schema.sections.map(section => section.key));
    const next: FormSchemaModel = {
      ...schema,
      sections: [
        ...schema.sections,
        {
          key,
          title: localizedText(this.t('dynamic.builder.section.defaultTitle'), this.editCultureService.editCulture()),
          fields: []
        }
      ]
    };
    this.schemaChange.emit(next);
    this.activeSectionChange.emit(key);
  }

  updateSectionTitleLocalized(sectionKey: string, title: FormSection['title']): void {
    const next = {
      ...this.schema(),
      sections: this.schema().sections.map(section =>
        section.key === sectionKey ? { ...section, title } : section
      )
    };
    this.schemaChange.emit(next);
  }

  updateSectionTitle(sectionKey: string, title: string): void {
    const next = {
      ...this.schema(),
      sections: this.schema().sections.map(section =>
        section.key === sectionKey
          ? { ...section, title: writeLocalizedText(section.title, this.editCultureService.editCulture(), title) }
          : section
      )
    };
    this.schemaChange.emit(next);
  }

  updateSectionColumns(sectionKey: string, columns: number | null): void {
    const next = {
      ...this.schema(),
      sections: this.schema().sections.map(section =>
        section.key === sectionKey
          ? { ...section, columns: columns ?? undefined }
          : section
      )
    };
    this.schemaChange.emit(next);
  }

  onSectionDrop(event: CdkDragDrop<FormSection[]>): void {
    if (event.previousIndex === event.currentIndex) {
      return;
    }
    const sections = [...this.schema().sections];
    moveItemInArray(sections, event.previousIndex, event.currentIndex);
    this.schemaChange.emit({ ...this.schema(), sections });
  }

  updateSectionConditional(sectionKey: string, conditional: FormConditional | undefined): void {
    const next = {
      ...this.schema(),
      sections: this.schema().sections.map(section =>
        section.key === sectionKey
          ? { ...section, conditional }
          : section
      )
    };
    this.schemaChange.emit(next);
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
